import { memo } from 'react'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import type { OntologyDomain } from '@/types/ontology'

interface ClassNodeData {
  label: string
  domain: OntologyDomain
  description?: string
  propertyCount: number
  instanceCount?: number
  isConnected?: boolean
}

const domainColors: Record<OntologyDomain, { bg: string; border: string; text: string; badgeBg: string }> = {
  core: {
    bg: '#fdf6e0',
    border: '#d4a020',
    text: '#9a7e1a',
    badgeBg: '#d4a020',
  },
  market: {
    bg: '#e0f5ef',
    border: '#5c8a6b',
    text: '#009b7d',
    badgeBg: '#5c8a6b',
  },
  business: {
    bg: '#e0eef8',
    border: '#3196e2',
    text: '#076ab8',
    badgeBg: '#3196e2',
  },
  solution: {
    bg: '#fdf6e0',
    border: '#ffc05f',
    text: '#d4a020',
    badgeBg: '#ffc05f',
  },
}

export const ClassNode = memo(({ data, selected }: NodeProps & { data: ClassNodeData }) => {
  const colors = domainColors[data.domain]
  const hasInstances = data.instanceCount !== undefined && data.instanceCount > 0

  return (
    <>
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#8a847a', width: 8, height: 8 }}
      />
      <div
        style={{
          padding: '12px 16px',
          borderRadius: '12px',
          border: `2px solid ${colors.border}`,
          backgroundColor: colors.bg,
          minWidth: '160px',
          boxShadow: selected ? `0 0 0 3px ${colors.border}40` : '0 2px 6px rgba(0,0,0,0.08)',
          transition: 'box-shadow 0.2s',
          cursor: 'pointer',
          position: 'relative',
        }}
      >
        {/* Instance count badge */}
        {hasInstances && (
          <div
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: colors.badgeBg,
              color: 'white',
              fontSize: '11px',
              fontWeight: 600,
              minWidth: '22px',
              height: '22px',
              borderRadius: '11px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 6px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
            }}
          >
            {data.instanceCount}
          </div>
        )}

        <div
          style={{
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: 'monospace',
            color: colors.text,
          }}
        >
          {data.label}
        </div>

        <div
          style={{
            fontSize: '10px',
            color: '#8a847a',
            marginTop: '4px',
            display: 'flex',
            gap: '8px',
          }}
        >
          {data.propertyCount > 0 && (
            <span>{data.propertyCount} props</span>
          )}
          {data.isConnected && data.instanceCount !== undefined && (
            <span style={{ color: hasInstances ? colors.text : '#a19a90' }}>
              {data.instanceCount} inst
            </span>
          )}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: '#8a847a', width: 8, height: 8 }}
      />
    </>
  )
})

ClassNode.displayName = 'ClassNode'
